import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  IconButton,
  Text,
  TextField,
  Toggle,
} from '@vibe/core';
import { Add, Delete, Search } from '@vibe/icons';
import {
  allowUser,
  removeUserAccess,
  type UserAccess,
} from '../../api/settings.api';
import UserPicker, { type PickedUser } from '../shared/UserPicker';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ManageAccessTabProps {
  users: UserAccess[];
  allUsersEnabled: boolean;
  onToggleAllUsers: (enabled: boolean) => void;
  onAddUser: (user: UserAccess) => void;
  onRemoveUser: (mondayUserId: string) => void;
  onUpdateUser: (user: UserAccess) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ManageAccessTab: React.FC<ManageAccessTabProps> = ({
  users,
  allUsersEnabled,
  onToggleAllUsers,
  onAddUser,
  onRemoveUser,
  onUpdateUser,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PickedUser | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Derived data ─────────────────────────────────────────────────────────

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        (u.userName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q),
    );
  }, [users, searchQuery]);

  const excludeUserIds = useMemo(
    () => users.map((u) => u.mondayUserId),
    [users],
  );

  // ─── Add user ─────────────────────────────────────────────────────────────

  const handleAddUser = useCallback(async () => {
    if (!selectedUser) return;
    setIsAdding(true);
    setError(null);
    try {
      const user = await allowUser(selectedUser.id);
      onAddUser({ ...user, userName: selectedUser.name, email: selectedUser.email });
      setSelectedUser(null);
      setShowAddForm(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to add user';
      setError(message);
    } finally {
      setIsAdding(false);
    }
  }, [selectedUser, onAddUser]);

  // ─── Remove user ──────────────────────────────────────────────────────────

  const handleRemoveUser = useCallback(
    async (mondayUserId: string) => {
      setError(null);
      try {
        await removeUserAccess(mondayUserId);
        onRemoveUser(mondayUserId);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to remove user';
        setError(message);
      }
    },
    [onRemoveUser],
  );

  // ─── Get role label ───────────────────────────────────────────────────────

  const getRoleLabel = (user: UserAccess): string => {
    if (user.isAdmin) return 'Admin';
    return 'Member';
  };

  // ─── Render: "All users" mode ─────────────────────────────────────────────

  if (allUsersEnabled) {
    return (
      <div className="rp-manage-access">
        <Flex
          justify={Flex.justify.SPACE_BETWEEN}
          align={Flex.align.CENTER}
          className="rp-manage-access__top-bar"
        >
          <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
            Who can access the app?
          </Text>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
            <Text type={Text.types.TEXT2}>All users</Text>
            <Toggle
              isSelected={allUsersEnabled}
              onChange={() => onToggleAllUsers(false)}
              size="small"
              ariaLabel="Toggle all users access"
            />
          </Flex>
        </Flex>

        <div className="rp-manage-access__all-users-state">
          <div className="rp-manage-access__all-users-illustration">
            <svg
              width="200"
              height="140"
              viewBox="0 0 200 140"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="60" cy="50" r="20" fill="#c3c4c7" />
              <circle cx="100" cy="40" r="22" fill="#a0a4ab" />
              <circle cx="140" cy="50" r="20" fill="#c3c4c7" />
              <rect x="40" y="75" width="40" height="50" rx="6" fill="#c3c4c7" />
              <rect x="78" y="65" width="44" height="60" rx="6" fill="#a0a4ab" />
              <rect x="120" y="75" width="40" height="50" rx="6" fill="#c3c4c7" />
            </svg>
          </div>
          <Text
            type={Text.types.TEXT1}
            weight={Text.weights.MEDIUM}
            style={{ color: '#676879', marginBottom: 16 }}
          >
            Full house! All members are in.
          </Text>
          <Button kind={Button.kinds.SECONDARY} size={Button.sizes.SMALL}>
            Check Plans
          </Button>
        </div>
      </div>
    );
  }

  // ─── Render: Specific users mode ──────────────────────────────────────────

  return (
    <div className="rp-manage-access">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-manage-access__top-bar"
      >
        <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
          Who can access the app?
        </Text>
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
          <Text type={Text.types.TEXT2}>All users</Text>
          <Toggle
            isSelected={allUsersEnabled}
            onChange={() => onToggleAllUsers(true)}
            size="small"
            ariaLabel="Toggle all users access"
          />
        </Flex>
      </Flex>

      {/* Action bar */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-manage-access__action-bar"
      >
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={() => setShowAddForm(true)}
        >
          Add users {users.length > 0 ? `${users.length}/Unlimited` : ''}
        </Button>
        <TextField
          size={TextField.sizes.SMALL}
          placeholder="Enter name to search"
          value={searchQuery}
          onChange={(value: string) => setSearchQuery(value)}
          iconName={Search}
          className="rp-manage-access__search"
        />
      </Flex>

      {/* Add user form */}
      {showAddForm && (
        <div className="rp-manage-access__add-form">
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            Add a new user
          </Text>
          <Flex gap={Flex.gaps.SMALL} align={Flex.align.END}>
            <div style={{ flex: 1 }}>
              <UserPicker
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="Search for a user…"
                excludeUserIds={excludeUserIds}
              />
            </div>
            <Button
              size={Button.sizes.SMALL}
              onClick={handleAddUser}
              loading={isAdding}
              disabled={!selectedUser}
            >
              Add
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              onClick={() => {
                setShowAddForm(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
          </Flex>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rp-permission-mgmt__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      {/* User table */}
      {filteredUsers.length === 0 ? (
        <div className="rp-permission-mgmt__empty">
          <Text>
            {users.length === 0
              ? 'No users added yet. Click "Add users" to grant access.'
              : 'No users match your search.'}
          </Text>
        </div>
      ) : (
        <table className="rp-settings-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th>Name</th>
              <th>Title</th>
              <th>Role</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, index) => (
              <tr key={user.mondayUserId}>
                <td>
                  <Text type={Text.types.TEXT2}>{index + 1}</Text>
                </td>
                <td>
                  <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
                    <div
                      className="rp-permission-mgmt__avatar"
                      style={{
                        backgroundColor: user.isAdmin ? '#e44258' : '#fdab3d',
                      }}
                    >
                      <Text
                        type={Text.types.TEXT2}
                        weight={Text.weights.BOLD}
                        style={{ color: '#fff', fontSize: 11 }}
                      >
                        {getInitials(user.userName)}
                      </Text>
                    </div>
                    <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                      {user.userName}
                    </Text>
                  </Flex>
                </td>
                <td>
                  <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                    {/* Title not stored in current entity, show dash */}
                    —
                  </Text>
                </td>
                <td>
                  <span
                    className={`rp-permission-mgmt__role-badge ${
                      user.isAdmin
                        ? 'rp-permission-mgmt__role-badge--admin'
                        : ''
                    }`}
                  >
                    {getRoleLabel(user)}
                  </span>
                </td>
                <td>
                  <IconButton
                    icon={Delete}
                    size={IconButton.sizes.SMALL}
                    onClick={() => handleRemoveUser(user.mondayUserId)}
                    ariaLabel={`Remove ${user.userName}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name[0] || '?').toUpperCase();
}

export default ManageAccessTab;
